/**
 * In-store mode (E10 / ladder item 13): a lightweight outbox for grocery
 * check-off mutations, scoped to EXACTLY that one action -- checking off an
 * item is the in-aisle gesture, and losing it to a dead spot in the store
 * is the one failure mode this exists to prevent. This is deliberately NOT
 * a general offline framework: no other mutation queues here.
 *
 * Shape: a failed check-off becomes a queue entry (in-memory, mirrored to
 * localStorage so a page reload in the store doesn't drop it). The UI keeps
 * the user's tap visible via `applyPendingCheckoffs` (a read-side overlay --
 * it never fights the query cache) plus a subtle "pending sync" indicator,
 * and `flushOutbox` retries when connectivity returns. Last write wins per
 * item: toggling the same line twice replaces the queued entry, and the
 * server's check-item endpoint takes an absolute `checked` value, so a
 * replay is idempotent.
 *
 * Everything in this module is pure (storage handles are injected) so the
 * queue/retry semantics are unit-testable without a browser.
 */

import type { GroceryCheckoffAnalyticsSnapshot } from "./grocery-checkoff-analytics";
import type { GroceryItemToggleResult } from "./api/types";
import {
  compareCheckoffOperationFreshness,
  parseCheckoffOperationId,
  resetCheckoffOperationClockForTests,
  type CheckoffOperationFreshness,
} from "./grocery-checkoff-operation";
import {
  publishCheckoffRetirementV3,
  isCheckoffOperationRetiredAtBaseline,
  purgeCheckoffRetirementTargetState,
  purgeConfirmedCheckoffRetirementTargetState,
  subscribeToCheckoffRetirements as subscribeToRetirementRecords,
  resetCheckoffRetirementsForTests,
  type EnumerableCheckoffStorage,
} from "./grocery-checkoff-retirements";
import {
  admitCheckoffEpochRecord,
  compareCheckoffEpochs,
  isCanonicalCheckoffEpoch,
  clearConfirmedServerCheckoff,
  rebaseLegacyCheckoff,
  type ServerConfirmedCheckoff,
} from "./grocery-checkoff-confirmed";
export {
  compareCheckoffOperationFreshness,
  createCheckoffOperationId,
  isCheckoffOperationNewerThan,
  parseCheckoffOperationId,
  resetCheckoffOperationClockForTests,
} from "./grocery-checkoff-operation";
export * from "./grocery-checkoff-retirements";

export interface CheckoffOutboxEntryV2 {
  schemaVersion: 2;
  /** Stable across persistence/remount; distinguishes same-ms repeated taps. */
  operationId: string;
  groceryListId: string;
  itemId: string;
  checked: boolean;
  /** Canonical PostgreSQL bigint text; never converted to Number. */
  checkoffEpoch: string;
  queuedAt: number;
  /** Minimal, privacy-safe state needed to recover completion analytics after reload. */
  analyticsSnapshot?: GroceryCheckoffAnalyticsSnapshot;
}

/** Read-only input from pre-causal clients. Never overwrite this namespace. */
export const LEGACY_CHECKOFF_OUTBOX_STORAGE_KEY = "bb.checkoff-outbox.v1";
export const CHECKOFF_OUTBOX_STORAGE_KEY = "bb.checkoff-outbox.v2";
/**
 * Existing hook call sites remain type-compatible while Task 7 wires the
 * authoritative baseline. Legacy shapes are migration inputs, never sendable.
 */
export type CheckoffOutboxEntry =
  | CheckoffOutboxEntryV2
  | {
      schemaVersion?: number;
      operationId?: string;
      groceryListId: string;
      itemId: string;
      checked: boolean;
      checkoffEpoch?: string;
      queuedAt: number;
      analyticsSnapshot?: GroceryCheckoffAnalyticsSnapshot;
      rebased?: boolean;
    };
export { CHECKOFF_RETIREMENTS_STORAGE_KEY } from "./grocery-checkoff-retirements";

export type CheckoffOperationRole = "owner" | "joined" | "stale";

export interface CoordinatedCheckoff {
  promise: Promise<GroceryItemToggleResult>;
  role: CheckoffOperationRole;
  /** Monotonic within one list/item lane; joined callers share the version. */
  version: number;
  /**
   * Stable by the time the coordinated request settles. Every same-target
   * caller contributes its directly captured boundary before its continuation
   * can run.
   */
  sharedCausalBoundary: () => string | undefined;
  isSuperseded: () => boolean;
  /** True when no later opposite target has replaced this optimistic intent. */
  isCurrentIntent: () => boolean;
  /** True only after this shared owner crossed its actual send boundary. */
  wasDispatched: () => boolean;
  /** Present only for an admitted server epoch; Task 7 carries it on-wire. */
  checkoffEpoch?: string;
  /** Exact operation identity owned by the shared request placed on wire. */
  operationId?: string;
}

interface CheckoffOperation {
  checked: boolean;
  version: number;
  promise: Promise<GroceryItemToggleResult>;
  superseded: boolean;
  skipDispatch: boolean;
  dispatchStarted: { current: boolean };
  settled: boolean;
  causalBoundary: { current: string | undefined };
  checkoffEpoch?: string;
  operationId?: string;
}

interface CheckoffLane {
  nextVersion: number;
  tail?: CheckoffOperation;
}

const checkoffLanes = new Map<string, CheckoffLane>();
function checkoffOperationFreshness(
  entry: CheckoffOutboxEntry,
): CheckoffOperationFreshness {
  return (
    parseCheckoffOperationId(entry.operationId) ?? {
      physicalTime:
        Number.isSafeInteger(entry.queuedAt) && entry.queuedAt >= 0
          ? entry.queuedAt
          : 0,
      logicalTime: "0",
      instanceId: `legacy-${persistedCheckoffOperationIdentity(entry)}`,
      sequence: "0",
      version: 2,
    }
  );
}

export function isCurrentCheckoffOutboxEntry(
  entry: CheckoffOutboxEntry,
): entry is CheckoffOutboxEntryV2 {
  return (
    entry.schemaVersion === 2 &&
    isCanonicalCheckoffEpoch(entry.checkoffEpoch) &&
    parseCheckoffOperationId(entry.operationId)?.version === 3
  );
}

/** Task 7 supplies the baseline obtained from GET/mutation, never storage. */
export function admitCheckoffOutboxEntry(
  entry: CheckoffOutboxEntry,
  baseline: ServerConfirmedCheckoff | undefined,
) {
  if (!isCurrentCheckoffOutboxEntry(entry)) {
    return { kind: "quarantine" as const, reason: "legacy" as const };
  }
  return admitCheckoffEpochRecord(
    { checkoffEpoch: entry.checkoffEpoch, operationId: entry.operationId },
    baseline,
  );
}

/**
 * Rebase is deliberately pure. The caller must persist the returned v2 record
 * only after its guarded authoritative response, and never retry a rebased
 * input after a second failure.
 */
export function rebaseLegacyOutboxEntry(
  entry: CheckoffOutboxEntry,
  baseline: ServerConfirmedCheckoff | undefined,
  operationId?: string,
  options: { retired?: boolean } = {},
): CheckoffOutboxEntryV2 | undefined {
  if (options.retired || isCurrentCheckoffOutboxEntry(entry)) return undefined;
  const result = rebaseLegacyCheckoff(entry, baseline, operationId);
  if (result.kind !== "rebase") return undefined;
  return {
    schemaVersion: 2,
    groceryListId: entry.groceryListId,
    itemId: entry.itemId,
    checked: result.checked,
    checkoffEpoch: result.checkoffEpoch,
    operationId: result.operationId,
    queuedAt: entry.queuedAt,
    analyticsSnapshot: entry.analyticsSnapshot,
  };
}

/** Only baseline-admitted state may become a coordinator owner. */
export function canEnterCheckoffLane(
  entry: CheckoffOutboxEntry,
  baseline: ServerConfirmedCheckoff | undefined,
): boolean {
  return admitCheckoffOutboxEntry(entry, baseline).kind === "admit";
}

export function classifyCheckoffOutboxFailure(
  entry: CheckoffOutboxEntry,
  error: unknown,
): { kind: "retry" | "drop" | "cleanup-target" } {
  if (!isCurrentCheckoffOutboxEntry(entry)) return { kind: "drop" };
  const status =
    typeof error === "object" && error !== null
      ? (error as { status?: unknown }).status
      : undefined;
  if (status === 404) return { kind: "cleanup-target" };
  return isRetryableCheckoffError(error) ? { kind: "retry" } : { kind: "drop" };
}

/**
 * Pure native-storage-event classification. Task 7 owns the guarded GET and
 * reload; this seam merely makes the higher/unconfirmed pause explicit.
 */
export function classifyCheckoffOutboxStorageEvent(
  raw: string | null,
  baselineFor:
    | ServerConfirmedCheckoff
    | undefined
    | ((groceryListId: string, itemId: string) => ServerConfirmedCheckoff | undefined),
):
  | { kind: "pause"; groceryListId: string; itemId: string }
  | { kind: "reload" }
  | { kind: "ignore" } {
  if (!raw) return { kind: "reload" };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return { kind: "ignore" };
    for (const candidate of parsed) {
      if (!candidate || typeof candidate !== "object") continue;
      if (!isExactV2OutboxEntry(candidate)) continue;
      const entry = candidate;
      const baseline =
        typeof baselineFor === "function"
          ? baselineFor(entry.groceryListId, entry.itemId)
          : baselineFor;
      const admission = admitCheckoffOutboxEntry(entry, baseline);
      if (admission.kind === "quarantine") {
        return {
          kind: "pause",
          groceryListId: entry.groceryListId,
          itemId: entry.itemId,
        };
      }
    }
    return { kind: "reload" };
  } catch {
    return { kind: "ignore" };
  }
}

export interface LoadedOutboxSource {
  namespace: "v2" | "v1";
  storageKey: typeof CHECKOFF_OUTBOX_STORAGE_KEY | typeof LEGACY_CHECKOFF_OUTBOX_STORAGE_KEY;
  /** Exact aggregate bytes observed before a receiver-safe disposition. */
  raw: string;
  /** Original candidates are retained for guarded legacy disposition. */
  candidates: readonly unknown[];
  entries: readonly CheckoffOutboxEntry[];
}

/**
 * Privacy-safe evidence about the durable aggregate reads used for policy
 * recovery. It deliberately contains no keys, bytes, or product identities.
 */
export interface CheckoffDurableSourceHealth {
  complete: boolean;
  malformed: boolean;
  unreadable: boolean;
}

export interface LoadedOutboxSourcesWithHealth {
  sources: LoadedOutboxSource[];
  health: CheckoffDurableSourceHealth;
}

function parseOutboxSource(raw: string | null, namespace: "v2" | "v1"): LoadedOutboxSource | undefined {
  if (raw === null) return undefined;
  try {
    const candidates: unknown = JSON.parse(raw);
    if (!Array.isArray(candidates)) return undefined;
    return {
      namespace,
      storageKey:
        namespace === "v2"
          ? CHECKOFF_OUTBOX_STORAGE_KEY
          : LEGACY_CHECKOFF_OUTBOX_STORAGE_KEY,
      raw,
      candidates,
      entries: candidates.filter(
        (candidate): candidate is CheckoffOutboxEntry =>
          isBasicOutboxEntry(candidate) &&
          (namespace === "v1" || isExactV2OutboxEntry(candidate)),
      ),
    };
  } catch {
    return undefined;
  }
}

function parseOutboxSourceWithHealth(
  raw: string | null,
  namespace: "v2" | "v1",
): { source: LoadedOutboxSource | undefined; malformed: boolean } {
  if (raw === null) return { source: undefined, malformed: false };
  try {
    const candidates: unknown = JSON.parse(raw);
    if (!Array.isArray(candidates)) return { source: undefined, malformed: true };
    const source = parseOutboxSource(raw, namespace);
    const malformed = candidates.some(
      (candidate) =>
        !isBasicOutboxEntry(candidate) ||
        (namespace === "v2" && !isExactV2OutboxEntry(candidate)),
    );
    return { source, malformed };
  } catch {
    return { source: undefined, malformed: true };
  }
}

type OutboxDispositionStorage =
  | (Partial<EnumerableCheckoffStorage> &
      Partial<Pick<Storage, "setItem" | "removeItem">>)
  | null
  | undefined;

const OUTBOX_RECOVERY_STORAGE_PREFIX = "bb.checkoff-outbox-recovery.v1:";
type OutboxRawSnapshot = { key: string; raw: string | null };
type OutboxRecoveryPhase = "prepared" | "writing" | "staged" | "disposing" | "disposed" | "committed" | "rolling-back" | "rolled-back";
interface OutboxRecoveryJournal {
  version: 2;
  ownerId: string;
  lockId: string;
  phase: OutboxRecoveryPhase;
  snapshots: OutboxRawSnapshot[];
  expectedAfterStage: OutboxRawSnapshot[];
  expectedAfterDispose: OutboxRawSnapshot[];
  expectedFinal: OutboxRawSnapshot[];
}
interface OutboxRecoveryHandle { key: string; raw: string; value: OutboxRecoveryJournal }
let outboxRecoveryOwnerFactory: (() => string) | undefined;

/** Test-only seam: production uses an unguessable owner whenever available. */
export function setOutboxRecoveryOwnerFactoryForTests(factory: (() => string) | undefined): void {
  outboxRecoveryOwnerFactory = factory;
}

function outboxLockId(snapshots: readonly OutboxRawSnapshot[]): string {
  return snapshots.map(({ key }) => key).sort().map(encodeURIComponent).join(":");
}
function isOutboxRecoverySnapshotKey(key: string): boolean { return key === LEGACY_CHECKOFF_OUTBOX_STORAGE_KEY || key === CHECKOFF_OUTBOX_STORAGE_KEY; }
function nextOutboxOwnerId(): string | undefined {
  const crypto = globalThis.crypto;
  if (outboxRecoveryOwnerFactory) return outboxRecoveryOwnerFactory();
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  if (typeof crypto?.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint32Array(4));
    return Array.from(bytes, (value) => value.toString(16).padStart(8, "0")).join("");
  }
  // A realm-local counter is not a lock owner. Refuse durable mutation when
  // the platform cannot provide entropy rather than inventing uniqueness.
  return undefined;
}
function outboxJournalKey(lockId: string, ownerId: string): string {
  return `${OUTBOX_RECOVERY_STORAGE_PREFIX}${lockId}:${encodeURIComponent(ownerId)}`;
}
function parseOutboxRecoveryJournal(raw: string | null, key?: string): OutboxRecoveryJournal | undefined {
  if (raw === null) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object") return undefined;
    const journal = value as Partial<OutboxRecoveryJournal>;
    const phases: OutboxRecoveryPhase[] = ["prepared", "writing", "staged", "disposing", "disposed", "committed", "rolling-back", "rolled-back"];
    const validSnapshots = (items: unknown): items is OutboxRawSnapshot[] => Array.isArray(items) && items.length > 0 && items.every((item) => !!item && typeof item === "object" && typeof (item as OutboxRawSnapshot).key === "string" && (typeof (item as OutboxRawSnapshot).raw === "string" || (item as OutboxRawSnapshot).raw === null));
    if (!(journal.version === 2 && typeof journal.ownerId === "string" && journal.ownerId.length > 0 && typeof journal.lockId === "string" && phases.includes(journal.phase as OutboxRecoveryPhase) && validSnapshots(journal.snapshots) && validSnapshots(journal.expectedAfterStage) && validSnapshots(journal.expectedAfterDispose) && validSnapshots(journal.expectedFinal))) return undefined;
    const snapshots = journal.snapshots!;
    const keys = snapshots.map((snapshot) => snapshot.key);
    const sameKeys = (items: OutboxRawSnapshot[]) => items.length === snapshots.length && items.every((item, index) => item.key === snapshots[index]?.key);
    if (new Set(keys).size !== keys.length || keys.some((entry) => !isOutboxRecoverySnapshotKey(entry)) || !sameKeys(journal.expectedAfterStage!) || !sameKeys(journal.expectedAfterDispose!) || !sameKeys(journal.expectedFinal!) || journal.lockId !== outboxLockId(snapshots) || (key !== undefined && key !== outboxJournalKey(journal.lockId, journal.ownerId))) return undefined;
    return journal as OutboxRecoveryJournal;
  } catch { return undefined; }
}
function updateOutboxRecovery(
  storage: Exclude<OutboxDispositionStorage, null | undefined>, handle: OutboxRecoveryHandle,
  patch: Partial<Omit<OutboxRecoveryJournal, "version" | "ownerId" | "lockId" | "snapshots">> & Pick<OutboxRecoveryJournal, "phase">,
): OutboxRecoveryHandle | undefined {
  try {
    const getItem = storage.getItem; const setItem = storage.setItem;
    if (typeof getItem !== "function" || typeof setItem !== "function" || getItem.call(storage, handle.key) !== handle.raw) return undefined;
    const value = { ...handle.value, ...patch };
    const raw = JSON.stringify(value);
    setItem.call(storage, handle.key, raw);
    return getItem.call(storage, handle.key) === raw ? { ...handle, raw, value } : undefined;
  } catch { return undefined; }
}
function outboxJournals(storage: Exclude<OutboxDispositionStorage, null | undefined>): { key: string; raw: string; value?: OutboxRecoveryJournal }[] | undefined {
  try {
    const keyAt = storage.key; const getItem = storage.getItem;
    if (typeof keyAt !== "function" || typeof getItem !== "function") return undefined;
    const found: { key: string; raw: string; value?: OutboxRecoveryJournal }[] = [];
    for (let index = 0; index < (storage.length ?? 0); index += 1) {
      const key = keyAt.call(storage, index);
      if (!key?.startsWith(OUTBOX_RECOVERY_STORAGE_PREFIX)) continue;
      const raw = getItem.call(storage, key);
      if (raw !== null) found.push({ key, raw, value: parseOutboxRecoveryJournal(raw, key) });
    }
    return found;
  } catch { return undefined; }
}
function outboxOverlaps(a: readonly OutboxRawSnapshot[], b: readonly OutboxRawSnapshot[]): boolean { return a.some((left) => b.some((right) => left.key === right.key)); }
function committedOutboxFenceMatchesCurrent(storage: Exclude<OutboxDispositionStorage, null | undefined>, journal: OutboxRecoveryJournal): boolean {
  try { const getItem = storage.getItem; return typeof getItem === "function" && journal.expectedFinal.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw); } catch { return false; }
}
function compatibleOutboxCommittedSuccessor(storage: Exclude<OutboxDispositionStorage, null | undefined>, journal: OutboxRecoveryJournal, successors: readonly OutboxRawSnapshot[]): boolean {
  return outboxOverlaps(journal.snapshots, successors) && committedOutboxFenceMatchesCurrent(storage, journal) && successors.filter((snapshot) => journal.expectedFinal.some((final) => final.key === snapshot.key)).every((snapshot) => journal.expectedFinal.find((final) => final.key === snapshot.key)?.raw === snapshot.raw);
}
function ownsOutboxRecovery(storage: Exclude<OutboxDispositionStorage, null | undefined>, handle: OutboxRecoveryHandle): boolean {
  const contenders = outboxJournals(storage);
  if (!contenders || contenders.some((candidate) => !candidate.value)) return false;
  const overlapping = contenders.filter((candidate) => candidate.value && outboxOverlaps(candidate.value.snapshots, handle.value.snapshots));
  const incumbent = overlapping.find((candidate) => candidate.value!.phase !== "prepared" && candidate.key !== handle.key);
  if (incumbent) return false;
  if (handle.value.phase !== "prepared") return getOutboxOwnRaw(storage, handle);
  const prepared = overlapping.filter((candidate) => candidate.value!.phase === "prepared").sort((a, b) => a.value!.ownerId.localeCompare(b.value!.ownerId));
  return getOutboxOwnRaw(storage, handle) && prepared[0]?.key === handle.key;
}
function getOutboxOwnRaw(storage: Exclude<OutboxDispositionStorage, null | undefined>, handle: OutboxRecoveryHandle): boolean {
  try { const getItem = storage.getItem; return typeof getItem === "function" && getItem.call(storage, handle.key) === handle.raw; } catch { return false; }
}
function beginOutboxRecovery(storage: Exclude<OutboxDispositionStorage, null | undefined>, snapshots: readonly OutboxRawSnapshot[], expectedAfterStage: readonly OutboxRawSnapshot[] = snapshots, expectedAfterDispose: readonly OutboxRawSnapshot[] = expectedAfterStage, expectedFinal: readonly OutboxRawSnapshot[] = expectedAfterDispose): OutboxRecoveryHandle | undefined {
  try {
    const getItem = storage.getItem; const setItem = storage.setItem;
    if (typeof getItem !== "function" || typeof setItem !== "function") return undefined;
    const canonical = [...snapshots].sort((a, b) => a.key.localeCompare(b.key));
    const lockId = outboxLockId(canonical);
    if (!canonical.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return undefined;
    const existing = outboxJournals(storage);
    if (!existing || existing.some((candidate) => !candidate.value)) return undefined;
    for (const candidate of existing) if (candidate.value!.phase === "committed" && outboxOverlaps(candidate.value!.snapshots, canonical)) {
      if (!clearOutboxCommittedFenceForSuccessor(storage, { key: candidate.key, raw: candidate.raw, value: candidate.value! }, canonical)) return undefined;
    }
    // A committed successor can only be cleared when its exact final state is
    // this operation's snapshot. Re-read after that cleanup: a reentrant
    // writer is otherwise able to make this stale owner journal a past raw.
    if (!canonical.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return undefined;
    const ownerId = nextOutboxOwnerId(); if (!ownerId) return undefined; const key = outboxJournalKey(lockId, ownerId);
    if (getItem.call(storage, key) !== null) return undefined;
    const reorder = (items: readonly OutboxRawSnapshot[]) => canonical.map((snapshot) => items.find((item) => item.key === snapshot.key) ?? snapshot);
    const value: OutboxRecoveryJournal = { version: 2, ownerId, lockId, phase: "prepared", snapshots: canonical, expectedAfterStage: reorder(expectedAfterStage), expectedAfterDispose: reorder(expectedAfterDispose), expectedFinal: reorder(expectedFinal) };
    const raw = JSON.stringify(value); setItem.call(storage, key, raw);
    const handle = getItem.call(storage, key) === raw ? { key, raw, value } : undefined;
    if (!handle) return undefined;
    if (ownsOutboxRecovery(storage, handle)) return handle;
    // A prepared loser is allowed to remove only its own verified record.
    cleanupOutboxRecovery(storage, handle);
    return undefined;
  } catch { return undefined; }
}
function cleanupOutboxRecovery(storage: Exclude<OutboxDispositionStorage, null | undefined>, journal: OutboxRecoveryHandle): boolean {
  try { const getItem = storage.getItem; const removeItem = storage.removeItem; if (typeof getItem !== "function" || typeof removeItem !== "function" || getItem.call(storage, journal.key) !== journal.raw) return false; removeItem.call(storage, journal.key); return getItem.call(storage, journal.key) === null; } catch { return false; }
}
function retireStaleOutboxRecoveryBehindCommittedFence(storage: Exclude<OutboxDispositionStorage, null | undefined>, journal: OutboxRecoveryHandle): boolean {
  const contenders = outboxJournals(storage);
  if (!contenders || contenders.some((candidate) => !candidate.value)) return false;
  const winner = contenders.find((candidate) => candidate.key !== journal.key && candidate.value!.phase === "committed" && outboxOverlaps(candidate.value!.snapshots, journal.value.snapshots) && committedOutboxFenceMatchesCurrent(storage, candidate.value!));
  if (!winner) return false;
  const staleOriginalsDiffer = journal.value.snapshots.some((snapshot) => winner.value!.expectedFinal.find((final) => final.key === snapshot.key)?.raw !== snapshot.raw);
  return staleOriginalsDiffer && cleanupOutboxRecovery(storage, journal);
}
function clearOutboxCommittedFenceForSuccessor(storage: Exclude<OutboxDispositionStorage, null | undefined>, fence: OutboxRecoveryHandle, successors: readonly OutboxRawSnapshot[]): boolean {
  if (!compatibleOutboxCommittedSuccessor(storage, fence.value, successors)) return false;
  const contenders = outboxJournals(storage);
  if (!contenders || contenders.some((candidate) => !candidate.value)) return false;
  for (const contender of contenders) {
    if (contender.key === fence.key || contender.value!.phase === "committed" || !outboxOverlaps(contender.value!.snapshots, fence.value.snapshots)) continue;
    if (!retireStaleOutboxRecoveryBehindCommittedFence(storage, { key: contender.key, raw: contender.raw, value: contender.value! })) return false;
  }
  const remaining = outboxJournals(storage);
  return !!remaining && remaining.every((candidate) => candidate.key === fence.key || !!candidate.value && (candidate.value.phase === "committed" || !outboxOverlaps(candidate.value.snapshots, fence.value.snapshots))) && cleanupOutboxRecovery(storage, fence);
}
function rollbackOutboxRecovery(storage: Exclude<OutboxDispositionStorage, null | undefined>, _snapshots: readonly OutboxRawSnapshot[], journal: OutboxRecoveryHandle, preserveObservedOriginal = false): boolean {
  if (!ownsOutboxRecovery(storage, journal)) { retireStaleOutboxRecoveryBehindCommittedFence(storage, journal); return false; }
  try {
    const getItem = storage.getItem;
    if (typeof getItem !== "function") return false;
    // Prepared is proof of intent only. A stale loser is never allowed to
    // interpret a winner's exact staged bytes as its own mutation.
    if (journal.value.phase === "prepared") {
      if (!journal.value.snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return false;
      return cleanupOutboxRecovery(storage, journal);
    }
  } catch { return false; }
  let rolling = updateOutboxRecovery(storage, journal, { phase: "rolling-back" });
  if (!rolling) return false;
  try {
    const getItem = storage.getItem; const setItem = storage.setItem; const removeItem = storage.removeItem;
    if (typeof getItem !== "function" || typeof setItem !== "function" || typeof removeItem !== "function") return false;
    let unresolved = false;
    for (const original of rolling.value.snapshots) {
      const current = getItem.call(storage, original.key);
      if (current === original.raw) { if (preserveObservedOriginal) unresolved = true; continue; }
      const expected = [rolling.value.expectedAfterStage, rolling.value.expectedAfterDispose, rolling.value.expectedFinal].some((state) => state.find((entry) => entry.key === original.key)?.raw === current);
      if (!expected) { unresolved = true; continue; }
      if (original.raw === null) removeItem.call(storage, original.key); else setItem.call(storage, original.key, original.raw);
      if (getItem.call(storage, original.key) !== original.raw) unresolved = true;
    }
    if (unresolved || !rolling.value.snapshots.every((entry) => getItem.call(storage, entry.key) === entry.raw)) return false;
    rolling = updateOutboxRecovery(storage, rolling, { phase: "rolled-back" });
    if (!rolling) return false;
    cleanupOutboxRecovery(storage, rolling); // terminal journal is intentional if cleanup is unavailable
    return true;
  } catch { return false; }
}
function finishOutboxRecovery(storage: Exclude<OutboxDispositionStorage, null | undefined>, journal: OutboxRecoveryHandle): boolean {
  const committed = updateOutboxRecovery(storage, journal, { phase: "committed" });
  if (!committed) return false;
  // Retained committed journal is the fence against a stale prepared owner.
  return true;
}

/** Resolves only journals whose exact state is known. Corrupt or concurrent records remain locked. */
export function resolveOutboxRecoveryJournals(storage: OutboxDispositionStorage): void {
  if (!storage) return;
  try {
    const keyAt = storage.key; const getItem = storage.getItem;
    if (typeof keyAt !== "function" || typeof getItem !== "function") return;
    const keys = Array.from({ length: storage.length ?? 0 }, (_, index) => keyAt.call(storage, index)).filter((key): key is string => !!key?.startsWith(OUTBOX_RECOVERY_STORAGE_PREFIX));
    for (const key of keys) {
      const raw = getItem.call(storage, key); const value = parseOutboxRecoveryJournal(raw, key);
      if (!value || raw === null) continue;
      const handle = { key, raw, value };
      if (value.phase === "committed") continue;
      if (value.phase === "rolled-back") { if (value.snapshots.every((entry) => getItem.call(storage, entry.key) === entry.raw)) cleanupOutboxRecovery(storage, handle); continue; }
      if (retireStaleOutboxRecoveryBehindCommittedFence(storage, handle)) continue;
      const current = value.snapshots.map((entry) => ({ key: entry.key, raw: getItem.call(storage, entry.key) }));
      const known = current.every((entry, index) => entry.raw === value.snapshots[index]!.raw || entry.raw === value.expectedAfterStage[index]!.raw || entry.raw === value.expectedAfterDispose[index]!.raw || entry.raw === value.expectedFinal[index]!.raw);
      if (value.phase === "prepared") {
        if (value.snapshots.every((entry) => getItem.call(storage, entry.key) === entry.raw)) cleanupOutboxRecovery(storage, handle);
      } else if ((value.phase === "writing" || value.phase === "staged" || value.phase === "disposing" || value.phase === "disposed" || value.phase === "rolling-back") && known) rollbackOutboxRecovery(storage, value.snapshots, handle);
    }
  } catch { /* recovery is best effort, never destructive */ }
}

/**
 * Receiver-safe disposition of one exact aggregate candidate. The loaded
 * source (including its raw bytes) and the candidate object identity are both
 * capabilities: callers cannot ask us to remove a semantically similar entry
 * from a later aggregate read.
 */
export function disposeLoadedOutboxSourceCandidate(
  storage: OutboxDispositionStorage,
  source: LoadedOutboxSource | undefined,
  rawCandidate: unknown,
  baseline: ServerConfirmedCheckoff | undefined,
  operationId: string | undefined,
): { durable: boolean; entry?: CheckoffOutboxEntryV2 } {
  if (!storage || !source || !isBasicOutboxEntry(rawCandidate)) {
    return { durable: false };
  }
  const candidateIndex = source.candidates.indexOf(rawCandidate);
  const promoted = rebaseLegacyOutboxEntry(rawCandidate, baseline, operationId);
  if (candidateIndex < 0 || !promoted) return { durable: false };
  let recovery:
    | { snapshots: OutboxRawSnapshot[]; journal: OutboxRecoveryHandle }
    | undefined;

  try {
    const getItem = storage.getItem;
    const setItem = storage.setItem;
    const removeItem = storage.removeItem;
    if (typeof getItem !== "function" || typeof setItem !== "function" || typeof removeItem !== "function") {
      return { durable: false };
    }
    // A source is only actionable if the exact loaded aggregate still exists.
    if (getItem.call(storage, source.storageKey) !== source.raw) {
      return { durable: false };
    }
    const v2Source =
      source.namespace === "v2"
        ? source
        : parseOutboxSource(
            getItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY),
            "v2",
          );
    if (source.namespace === "v1" && getItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY) !== null && !v2Source) {
      // A malformed v2 aggregate cannot be safely rewritten while preserving
      // its raw candidates, so retain the v1 source untouched.
      return { durable: false };
    }
    const snapshots: OutboxRawSnapshot[] = source.namespace === "v2"
      ? [{ key: source.storageKey, raw: source.raw }]
      : [
          { key: source.storageKey, raw: source.raw },
          { key: CHECKOFF_OUTBOX_STORAGE_KEY, raw: v2Source?.raw ?? null },
        ];
    const stagedV2 = [...(v2Source?.candidates ?? []), promoted];
    const stagedV2Raw = JSON.stringify(stagedV2);
    const stagedSnapshots = snapshots.map((snapshot) =>
      snapshot.key === CHECKOFF_OUTBOX_STORAGE_KEY
        ? { ...snapshot, raw: stagedV2Raw }
        : snapshot,
    );
    const retainedSourceCandidates = source.candidates.filter(
      (_candidate, index) => index !== candidateIndex,
    );
    const sourceAfterDeletion =
      source.namespace === "v2"
        ? JSON.stringify([
            ...retainedSourceCandidates,
            promoted,
          ])
        : retainedSourceCandidates.length === 0
          ? null
          : JSON.stringify(retainedSourceCandidates);
    const disposedSnapshots = stagedSnapshots.map((snapshot) =>
      snapshot.key === source.storageKey
        ? { ...snapshot, raw: sourceAfterDeletion }
        : snapshot,
    );
    const journal = beginOutboxRecovery(storage, snapshots, stagedSnapshots, disposedSnapshots, disposedSnapshots);
    if (!journal) return { durable: false };
    recovery = { snapshots, journal };
    let activeJournal = journal;
    const fail = () => { rollbackOutboxRecovery(storage, snapshots, activeJournal, true); return { durable: false }; };
    if (!ownsOutboxRecovery(storage, activeJournal) || !snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return fail();
    activeJournal = updateOutboxRecovery(storage, activeJournal, { phase: "writing" }) ?? activeJournal;
    recovery.journal = activeJournal;
    if (activeJournal.value.phase !== "writing" || !ownsOutboxRecovery(storage, activeJournal) || !snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return fail();
    setItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY, stagedV2Raw);
    if (getItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY) !== stagedV2Raw) return fail();
    activeJournal = updateOutboxRecovery(storage, activeJournal, { phase: "staged" }) ?? activeJournal;
    recovery.journal = activeJournal;
    if (activeJournal.value.phase !== "staged" || !ownsOutboxRecovery(storage, activeJournal) || getItem.call(storage, source.storageKey) !== stagedSnapshots.find((snapshot) => snapshot.key === source.storageKey)?.raw) return fail();
    activeJournal = updateOutboxRecovery(storage, activeJournal, { phase: "disposing" }) ?? activeJournal;
    recovery.journal = activeJournal;
    if (activeJournal.value.phase !== "disposing" || !ownsOutboxRecovery(storage, activeJournal) || getItem.call(storage, source.storageKey) !== stagedSnapshots.find((snapshot) => snapshot.key === source.storageKey)?.raw) return fail();
    // The v2 source was intentionally staged with its original candidate, so
    // this second write is the independently verified source deletion.
    if (sourceAfterDeletion === null) {
      removeItem.call(storage, source.storageKey);
    } else {
      setItem.call(storage, source.storageKey, sourceAfterDeletion);
    }
    if (getItem.call(storage, source.storageKey) !== sourceAfterDeletion) {
      return fail();
    }
    activeJournal = updateOutboxRecovery(storage, activeJournal, { phase: "disposed" }) ?? activeJournal;
    recovery.journal = activeJournal;
    if (activeJournal.value.phase !== "disposed") return fail();
    return finishOutboxRecovery(storage, activeJournal)
      ? { durable: true, entry: promoted }
      : { durable: false };
  } catch {
    if (recovery) rollbackOutboxRecovery(storage, recovery.snapshots, recovery.journal, true);
    return { durable: false };
  }
}

/** Removes exactly one loaded aggregate candidate with an exact postcondition. */
export function removeLoadedOutboxSourceCandidate(
  storage: OutboxDispositionStorage,
  source: LoadedOutboxSource | undefined,
  rawCandidate: unknown,
): boolean {
  if (!storage || !source) return false;
  const index = source.candidates.indexOf(rawCandidate);
  if (index < 0) return false;
  try {
    const getItem = storage.getItem;
    const setItem = storage.setItem;
    const removeItem = storage.removeItem;
    if (typeof getItem !== "function" || typeof setItem !== "function") return false;
    if (getItem.call(storage, source.storageKey) !== source.raw) return false;
    const retained = source.candidates.filter((_candidate, candidateIndex) => candidateIndex !== index);
    const nextRaw = retained.length === 0 ? null : JSON.stringify(retained);
    if (nextRaw === null) {
      if (typeof removeItem !== "function") return false;
      removeItem.call(storage, source.storageKey);
    } else {
      setItem.call(storage, source.storageKey, nextRaw);
    }
    if (getItem.call(storage, source.storageKey) === nextRaw) return true;
    try {
      setItem.call(storage, source.storageKey, source.raw);
    } catch {
      /* source may already have survived a throwing mutation */
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Recovery ordering for a legacy source: a real, baseline-compatible
 * retirement is physically consumed first; only an uncovered source can be
 * promoted/rebased. A failed deletion is a wait, never a blind rebase.
 */
export function recoverLoadedOutboxSourceCandidate(
  storage: OutboxDispositionStorage,
  source: LoadedOutboxSource | undefined,
  rawCandidate: unknown,
  baseline: ServerConfirmedCheckoff | undefined,
  operationId: string | undefined,
):
  | { kind: "drop" }
  | { kind: "wait" }
  | { kind: "rebase"; entry: CheckoffOutboxEntryV2 } {
  if (!isBasicOutboxEntry(rawCandidate)) return { kind: "wait" };
  if (
    baseline &&
    parseCheckoffOperationId(rawCandidate.operationId)?.version === 3 &&
    isCheckoffOperationRetiredAtBaseline(
      storage,
      {
        groceryListId: rawCandidate.groceryListId,
        itemId: rawCandidate.itemId,
        checkoffEpoch: baseline.checkoffEpoch,
        operationId: rawCandidate.operationId!,
      },
      baseline,
    )
  ) {
    return removeLoadedOutboxSourceCandidate(storage, source, rawCandidate)
      ? { kind: "drop" }
      : { kind: "wait" };
  }
  const disposition = disposeLoadedOutboxSourceCandidate(
    storage,
    source,
    rawCandidate,
    baseline,
    operationId,
  );
  return disposition.durable && disposition.entry
    ? { kind: "rebase", entry: disposition.entry }
    : { kind: "wait" };
}

/** Reads both durable namespaces without promotion, deletion, or authority. */
export function loadOutboxSources(
  storage: (Partial<EnumerableCheckoffStorage> & Partial<Pick<Storage, "setItem" | "removeItem">>) | null | undefined,
): LoadedOutboxSource[] {
  return loadOutboxSourcesWithHealth(storage).sources;
}

/**
 * Loads both durable outbox namespaces and reports whether any relevant bytes
 * could not be safely interpreted. A parser-invalid candidate is retained in
 * storage and marked malformed; callers performing policy recovery must not
 * treat the valid subset as the complete causal source set.
 */
export function loadOutboxSourcesWithHealth(
  storage: (Partial<EnumerableCheckoffStorage> & Partial<Pick<Storage, "setItem" | "removeItem">>) | null | undefined,
): LoadedOutboxSourcesWithHealth {
  const health: CheckoffDurableSourceHealth = {
    complete: true,
    malformed: false,
    unreadable: false,
  };
  const sources: LoadedOutboxSource[] = [];
  try {
    const getItem = storage?.getItem;
    if (!storage || typeof getItem !== "function") {
      return { sources, health: { complete: false, malformed: false, unreadable: true } };
    }
    resolveOutboxRecoveryJournals(storage);
    for (const [key, namespace] of [
      [CHECKOFF_OUTBOX_STORAGE_KEY, "v2"],
      [LEGACY_CHECKOFF_OUTBOX_STORAGE_KEY, "v1"],
    ] as const) {
      let raw: string | null;
      try {
        raw = getItem.call(storage, key);
      } catch {
        health.complete = false;
        health.unreadable = true;
        continue;
      }
      const parsed = parseOutboxSourceWithHealth(raw, namespace);
      health.malformed ||= parsed.malformed;
      if (parsed.source) sources.push(parsed.source);
    }
  } catch {
    health.complete = false;
    health.unreadable = true;
  }
  if (health.malformed) health.complete = false;
  return { sources, health };
}

function isSupportedPhysicalTime(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 8_640_000_000_000_000
  );
}

function isBasicOutboxEntry(value: unknown): value is CheckoffOutboxEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as CheckoffOutboxEntry;
  return (
    isValidOutboxId(entry.groceryListId) &&
    isValidOutboxId(entry.itemId) &&
    typeof entry.checked === "boolean" &&
    isSupportedPhysicalTime(entry.queuedAt) &&
    isValidAnalyticsSnapshot(entry.analyticsSnapshot)
  );
}

function isValidOutboxId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !value.includes(":") &&
    !value.includes("\\") &&
    !value.includes("\0")
  );
}

function isExactV2OutboxEntry(value: unknown): value is CheckoffOutboxEntryV2 {
  if (!isBasicOutboxEntry(value)) return false;
  const entry = value as Record<string, unknown>;
  const keys = Object.keys(entry);
  const required = ["schemaVersion", "groceryListId", "itemId", "checked", "checkoffEpoch", "operationId", "queuedAt"];
  if (
    !required.every((key) => keys.includes(key)) ||
    !keys.every((key) => [...required, "analyticsSnapshot"].includes(key))
  )
    return false;
  return isCurrentCheckoffOutboxEntry(value);
}

/**
 * Records a successful direct intent before any stale hook callback can
 * republish its older queue snapshot. The module registry synchronizes hooks
 * in this realm; localStorage carries the same cutoff across tabs/reloads.
 */
export function retireCheckoffOperationsThrough(
  storage:
    Pick<Storage, "getItem" | "setItem" | "removeItem"> | null | undefined,
  groceryListId: string,
  itemId: string,
  supersededThroughOperationId: string,
  supersededThroughEpoch?: string,
): void {
  if (!isCanonicalCheckoffEpoch(supersededThroughEpoch)) return;
  publishCheckoffRetirementV3(storage, {
    schemaVersion: 3,
    groceryListId,
    itemId,
    supersededThroughEpoch,
    supersededThroughOperationId,
  });
}

export function subscribeToCheckoffRetirements(
  listener: () => void,
): () => void {
  return subscribeToRetirementRecords(listener);
}

function checkoffLaneKey(groceryListId: string, itemId: string): string {
  return `${groceryListId}:${itemId}`;
}

function skipCheckoffTargetLane(groceryListId: string, itemId: string): number {
  const key = checkoffLaneKey(groceryListId, itemId);
  const lane = checkoffLanes.get(key);
  if (!lane?.tail) return 0;
  lane.tail.superseded = true;
  lane.tail.skipDispatch = true;
  checkoffLanes.delete(key);
  return 1;
}

function maximumCheckoffCausalBoundary(
  current: string | undefined,
  candidate: string | undefined,
): string | undefined {
  const candidateFreshness = parseCheckoffOperationId(candidate);
  if (!candidateFreshness) return current;
  const currentFreshness = parseCheckoffOperationId(current);
  if (!currentFreshness) return candidate;
  return compareCheckoffOperationFreshness(
    candidateFreshness,
    currentFreshness,
  ) > 0
    ? candidate
    : current;
}

function isStrictlyNewerCheckoffCausalBoundary(
  candidate: string | undefined,
  current: string | undefined,
): boolean {
  const candidateFreshness = parseCheckoffOperationId(candidate);
  const currentFreshness = parseCheckoffOperationId(current);
  return (
    !!candidateFreshness &&
    !!currentFreshness &&
    compareCheckoffOperationFreshness(candidateFreshness, currentFreshness) > 0
  );
}

/**
 * Coordinates every browser check-off for one list item. Equal adjacent
 * targets share one request; opposite targets wait behind the prior target so
 * the newest intent is the last value committed by the server.
 */
export function coordinateCheckoff(
  groceryListId: string,
  itemId: string,
  checked: boolean,
  send: (
    sharedCausalBoundary: () => string | undefined,
  ) => Promise<GroceryItemToggleResult>,
  causalBoundary?: string,
  checkoffEpoch?: string,
  operationId?: string,
): CoordinatedCheckoff {
  const initialCausalBoundary = maximumCheckoffCausalBoundary(
    undefined,
    causalBoundary,
  );
  // Hooks only run in the browser. Avoid retaining request state if this
  // module is ever imported and invoked during SSR.
  if (typeof window === "undefined") {
    const dispatchStarted = { current: false };
    const sharedCausalBoundary = () => initialCausalBoundary;
    return {
      promise: Promise.resolve().then(() => {
        dispatchStarted.current = true;
        return send(sharedCausalBoundary);
      }),
      role: "owner",
      version: 0,
      sharedCausalBoundary,
      isSuperseded: () => false,
      isCurrentIntent: () => true,
      wasDispatched: () => dispatchStarted.current,
      operationId,
    };
  }

  const key = checkoffLaneKey(groceryListId, itemId);
  const lane = checkoffLanes.get(key) ?? { nextVersion: 0 };
  const tail = lane.tail;

  const candidateEpoch = isCanonicalCheckoffEpoch(checkoffEpoch)
    ? checkoffEpoch
    : undefined;
  const tailEpoch = tail?.checkoffEpoch;
  if (tail && !tail.settled && candidateEpoch && tailEpoch) {
    const epochOrder = compareCheckoffEpochs(candidateEpoch, tailEpoch);
    if (epochOrder < 0) {
      // A lower epoch must not join, replace, or settle the currently admitted
      // owner. Task 7 treats this pure result as a stale completion.
      return {
        promise: Promise.resolve(undefined as unknown as GroceryItemToggleResult),
        role: "stale",
        version: tail.version,
        sharedCausalBoundary: () => tail.causalBoundary.current,
        isSuperseded: () => true,
        isCurrentIntent: () => false,
        wasDispatched: () => false,
        checkoffEpoch: candidateEpoch,
        operationId,
      };
    }
    // A higher server epoch owns its own queued operation even when the UI
    // target is identical. It must never join an on-wire lower epoch.
    if (epochOrder > 0) {
      tail.superseded = true;
      tail.skipDispatch = true;
    }
  }

  const sameTargetAndCompatibleEpoch =
    tail &&
    !tail.settled &&
    tail.checked === checked &&
    (!candidateEpoch || !tailEpoch || candidateEpoch === tailEpoch);
  if (sameTargetAndCompatibleEpoch) {
    const queuedSameEpochReplacement =
      !!candidateEpoch &&
      candidateEpoch === tailEpoch &&
      !tail.dispatchStarted.current &&
      isStrictlyNewerCheckoffCausalBoundary(
        causalBoundary,
        tail.causalBoundary.current,
      );
    if (queuedSameEpochReplacement) {
      // The older owner has not crossed the wire, so a strictly newer direct
      // boundary can preserve its own send callback and identity. Do not merge
      // that boundary into the skipped predecessor.
      tail.superseded = true;
      tail.skipDispatch = true;
    } else {
      tail.causalBoundary.current = maximumCheckoffCausalBoundary(
        tail.causalBoundary.current,
        causalBoundary,
      );
      return {
        promise: tail.promise,
        role: "joined",
        version: tail.version,
        sharedCausalBoundary: () => tail.causalBoundary.current,
        isSuperseded: () => tail.superseded,
        isCurrentIntent: () => !tail.superseded,
        wasDispatched: () => tail.dispatchStarted.current,
        checkoffEpoch: tailEpoch,
        operationId: tail.operationId,
      };
    }
  }

  if (tail && !tail.settled) tail.superseded = true;

  const operationBoundary = { current: initialCausalBoundary };
  const dispatchStarted = { current: false };
  const sharedCausalBoundary = () => operationBoundary.current;
  // `start` closes over the operation before its promise can be constructed.
  // eslint-disable-next-line prefer-const
  let operation: CheckoffOperation | undefined;
  const start = () => {
    // A newer queued owner may supersede this operation before its predecessor
    // settles. Do not dispatch a known-stale request merely to serialize it.
    if (operation?.skipDispatch) {
      return Promise.resolve(undefined as unknown as GroceryItemToggleResult);
    }
    dispatchStarted.current = true;
    try {
      return Promise.resolve(send(sharedCausalBoundary));
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const promise = tail ? tail.promise.then(start, start) : start();
  operation = {
    checked,
    version: ++lane.nextVersion,
    promise,
    superseded: false,
    skipDispatch: false,
    dispatchStarted,
    settled: false,
    causalBoundary: operationBoundary,
    checkoffEpoch: candidateEpoch,
    operationId,
  };
  lane.tail = operation;
  checkoffLanes.set(key, lane);

  const cleanup = () => {
    // This reaction is registered before coordinateCheckoff returns. It
    // closes membership before any awaiting hook continuation can publish a
    // success, so every callback sees one frozen request maximum. A caller
    // arriving after this point starts a new coordinated request.
    operation.settled = true;
    if (checkoffLanes.get(key) === lane && lane.tail === operation) {
      checkoffLanes.delete(key);
    }
  };
  void promise.then(cleanup, cleanup);

  return {
    promise,
    role: "owner",
    version: operation.version,
    sharedCausalBoundary,
    isSuperseded: () => operation!.superseded,
    isCurrentIntent: () => !operation!.superseded,
    wasDispatched: () => dispatchStarted.current,
    checkoffEpoch: candidateEpoch,
    operationId,
  };
}

/** Removes only stale process-local owners for one authoritative higher epoch. */
export function purgeConfirmedCheckoffTarget(
  groceryListId: string,
  itemId: string,
  confirmedEpoch: string,
): number {
  if (!isCanonicalCheckoffEpoch(confirmedEpoch)) return 0;
  const key = checkoffLaneKey(groceryListId, itemId);
  const lane = checkoffLanes.get(key);
  const tailEpoch = lane?.tail?.checkoffEpoch;
  if (!lane || !tailEpoch || compareCheckoffEpochs(tailEpoch, confirmedEpoch) >= 0) return 0;
  lane.tail!.superseded = true;
  lane.tail!.skipDispatch = true;
  checkoffLanes.delete(key);
  return 1;
}

/**
 * Terminal target cleanup for a server 404. This intentionally does not use
 * an epoch cutoff: no equal or higher local record may be replayed for a
 * resource the server says no longer exists.
 */
export function purgeDeletedCheckoffTargetState(
  storage: OutboxDispositionStorage,
  groceryListId: string,
  itemId: string,
): { lanes: number; outbox: number; retirements: number } {
  // Skip before any durable work so a queued successor cannot dispatch after
  // its predecessor settles during the storage pass.
  const lanes = skipCheckoffTargetLane(groceryListId, itemId);
  let outbox = 0;
  if (storage) {
    try {
      const getItem = storage.getItem;
      const setItem = storage.setItem;
      const removeItem = storage.removeItem;
      if (
        typeof getItem === "function" &&
        typeof setItem === "function" &&
        typeof removeItem === "function"
      ) {
        for (const namespace of ["v2", "v1"] as const) {
          const key =
            namespace === "v2"
              ? CHECKOFF_OUTBOX_STORAGE_KEY
              : LEGACY_CHECKOFF_OUTBOX_STORAGE_KEY;
          const source = parseOutboxSource(getItem.call(storage, key), namespace);
          if (!source) continue;
          const retained = source.candidates.filter(
            (candidate) =>
              !isBasicOutboxEntry(candidate) ||
              candidate.groceryListId !== groceryListId ||
              candidate.itemId !== itemId,
          );
          if (retained.length === source.candidates.length) continue;
          const nextRaw = retained.length === 0 ? null : JSON.stringify(retained);
          const finalSnapshots = [{ key, raw: nextRaw }];
          const journal = beginOutboxRecovery(storage, [{ key, raw: source.raw }], finalSnapshots, finalSnapshots, finalSnapshots);
          if (!journal) continue;
          try {
            if (!ownsOutboxRecovery(storage, journal) || getItem.call(storage, key) !== source.raw) {
              rollbackOutboxRecovery(storage, [{ key, raw: source.raw }], journal);
              continue;
            }
            const writing = updateOutboxRecovery(storage, journal, { phase: "writing" });
            if (!writing || !ownsOutboxRecovery(storage, writing) || getItem.call(storage, key) !== source.raw) {
              rollbackOutboxRecovery(storage, [{ key, raw: source.raw }], writing ?? journal);
              continue;
            }
            if (nextRaw === null) removeItem.call(storage, key);
            else setItem.call(storage, key, nextRaw);
            if (getItem.call(storage, key) === nextRaw) {
              const staged = updateOutboxRecovery(storage, writing, { phase: "staged" });
              if (staged && finishOutboxRecovery(storage, staged)) {
                outbox += source.candidates.length - retained.length;
              } else {
                rollbackOutboxRecovery(storage, [{ key, raw: source.raw }], staged ?? writing);
              }
            } else {
              rollbackOutboxRecovery(storage, [{ key, raw: source.raw }], writing);
            }
          } catch {
            rollbackOutboxRecovery(storage, [{ key, raw: source.raw }], journal);
          }
        }
      }
    } catch {
      /* only independently verified removals count */
    }
  }
  const retirements = purgeCheckoffRetirementTargetState(storage, groceryListId, itemId);
  clearConfirmedServerCheckoff(groceryListId, itemId);
  return { lanes, outbox, retirements };
}

/**
 * Target-scoped pure purge for an authoritative higher response/404 recovery.
 * It marks an in-memory queued tail before removing its registry entry and
 * rewrites only the v2 aggregate after an exact target filter.
 */
export function purgeConfirmedCheckoffTargetState(
  storage: (Partial<EnumerableCheckoffStorage> & Partial<Pick<Storage, "setItem" | "removeItem">>) | null | undefined,
  groceryListId: string,
  itemId: string,
  confirmedEpoch: string,
): { lanes: number; outbox: number } {
  const lanes = purgeConfirmedCheckoffTarget(groceryListId, itemId, confirmedEpoch);
  purgeConfirmedCheckoffRetirementTargetState(
    storage,
    groceryListId,
    itemId,
    confirmedEpoch,
  );
  if (!storage || !isCanonicalCheckoffEpoch(confirmedEpoch)) return { lanes, outbox: 0 };
  try {
    const getItem = storage.getItem;
    const setItem = storage.setItem;
    const removeItem = storage.removeItem;
    if (typeof getItem !== "function" || typeof setItem !== "function" || typeof removeItem !== "function") return { lanes, outbox: 0 };
    // A prepared recovery owner deliberately has no destructive authority. If
    // an in-flight publisher changes the aggregate after it was captured,
    // abandon that prepared journal and reacquire from the new exact bytes.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const source = parseOutboxSource(getItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY), "v2");
      if (!source) return { lanes, outbox: 0 };
      const retained = source.candidates.filter((candidate) =>
        !isExactV2OutboxEntry(candidate) ||
        candidate.groceryListId !== groceryListId ||
        candidate.itemId !== itemId ||
        compareCheckoffEpochs(candidate.checkoffEpoch, confirmedEpoch) >= 0,
      );
      const removed = source.candidates.length - retained.length;
      if (removed === 0) return { lanes, outbox: 0 };
      const journal = beginOutboxRecovery(storage, [{ key: source.storageKey, raw: source.raw }]);
      if (!journal) return { lanes, outbox: 0 };
      const latestRaw = getItem.call(storage, source.storageKey);
      if (latestRaw !== source.raw) {
        cleanupOutboxRecovery(storage, journal);
        continue;
      }
      const latest = parseOutboxSource(latestRaw, "v2");
      if (!latest) { cleanupOutboxRecovery(storage, journal); return { lanes, outbox: 0 }; }
      const latestRetained = latest.candidates.filter((candidate) =>
        !isExactV2OutboxEntry(candidate) ||
        candidate.groceryListId !== groceryListId ||
        candidate.itemId !== itemId ||
        compareCheckoffEpochs(candidate.checkoffEpoch, confirmedEpoch) >= 0,
      );
      const latestRemoved = latest.candidates.length - latestRetained.length;
      if (latestRemoved === 0) { cleanupOutboxRecovery(storage, journal); return { lanes, outbox: 0 }; }
      const nextRaw = latestRetained.length === 0 ? null : JSON.stringify(latestRetained);
      const expected = [{ key: source.storageKey, raw: nextRaw }];
      const writing = updateOutboxRecovery(storage, journal, { phase: "writing", expectedAfterStage: expected, expectedAfterDispose: expected, expectedFinal: expected });
      if (!writing || !ownsOutboxRecovery(storage, writing) || getItem.call(storage, source.storageKey) !== latestRaw) { rollbackOutboxRecovery(storage, [{ key: source.storageKey, raw: source.raw }], writing ?? journal); return { lanes, outbox: 0 }; }
      if (nextRaw === null) removeItem.call(storage, source.storageKey); else setItem.call(storage, source.storageKey, nextRaw);
      if (getItem.call(storage, source.storageKey) !== nextRaw) { rollbackOutboxRecovery(storage, [{ key: source.storageKey, raw: source.raw }], writing); return { lanes, outbox: 0 }; }
      const staged = updateOutboxRecovery(storage, writing, { phase: "staged" });
      if (!staged || !finishOutboxRecovery(storage, staged)) { rollbackOutboxRecovery(storage, [{ key: source.storageKey, raw: source.raw }], staged ?? writing); return { lanes, outbox: 0 }; }
      return { lanes, outbox: latestRemoved };
    }
    return { lanes, outbox: 0 };
  } catch {
    return { lanes, outbox: 0 };
  }
}

/** Test isolation for the process-local coordination registry. */
export function resetCheckoffLanes(): void {
  checkoffLanes.clear();
  resetCheckoffRetirementsForTests();
  resetCheckoffOperationClockForTests();
}

/** Backward-compatible test helper name while callers migrate to lanes. */
export const resetInFlightCheckoffs = resetCheckoffLanes;

/**
 * Adds (or replaces -- last write wins per itemId) one queued check-off.
 * Pure; returns a new array.
 */
export function enqueueCheckoff(
  entries: readonly CheckoffOutboxEntry[],
  entry: CheckoffOutboxEntry,
): CheckoffOutboxEntry[] {
  return [...entries.filter((e) => e.itemId !== entry.itemId), entry];
}

/**
 * The pending checked-state overlay for ONE grocery list: itemId -> the
 * checked value the user chose but the server hasn't confirmed yet.
 */
export function pendingCheckedState(
  entries: readonly CheckoffOutboxEntry[],
  groceryListId: string | undefined,
): Map<string, boolean> {
  const map = new Map<string, boolean>();
  if (!groceryListId) return map;
  for (const entry of entries) {
    if (entry.groceryListId === groceryListId) {
      map.set(entry.itemId, entry.checked);
    }
  }
  return map;
}

/** Baseline-gated overlay used by Task 7; raw durable state is never enough. */
export function pendingConfirmedCheckedState(
  entries: readonly CheckoffOutboxEntry[],
  groceryListId: string | undefined,
  baselineFor: (itemId: string) => ServerConfirmedCheckoff | undefined,
): Map<string, boolean> {
  const admitted = entries.filter(
    (entry) => admitCheckoffOutboxEntry(entry, baselineFor(entry.itemId)).kind === "admit",
  );
  return pendingCheckedState(admitted, groceryListId);
}

/**
 * Read-side overlay: items with any pending check-off's chosen state
 * applied. Never mutates; items without a pending entry keep their
 * reference. This is how the UI keeps the user's tap through rollbacks,
 * poll refetches, and reloads -- the query cache itself is never patched
 * by the outbox.
 */
export function applyPendingCheckoffs<
  T extends { id: string; checked: boolean },
>(items: readonly T[], pending: ReadonlyMap<string, boolean>): T[] {
  if (pending.size === 0) return items as T[];
  return items.map((item) => {
    const checked = pending.get(item.id);
    return checked === undefined || checked === item.checked
      ? item
      : { ...item, checked };
  });
}

/**
 * True when this failure is worth queueing and retrying: the request never
 * got a real server verdict (network failure -- apiFetch wraps those as
 * status 0 -- or no status at all), or the server was transiently unable
 * to answer (5xx, 408, 429). A definite 4xx verdict is NOT retryable: the
 * server rejected the write, and replaying it would just fail forever.
 */
export function isRetryableCheckoffError(error: unknown): boolean {
  const status =
    typeof error === "object" && error !== null
      ? (error as { status?: unknown }).status
      : undefined;
  if (typeof status !== "number" || status === 0) return true;
  return status >= 500 || status === 408 || status === 429;
}

export interface FlushResult {
  remaining: CheckoffOutboxEntry[];
  sentCount: number;
}

/**
 * Attempts to send every queued entry, oldest first.
 *  - success: entry leaves the queue
 *  - non-retryable failure (a real 4xx verdict): entry leaves the queue --
 *    the server said no, and the next refetch shows the true state
 *  - retryable failure: the entry AND everything after it stay queued, and
 *    the flush stops (if the network is down, every further send would
 *    fail the same way)
 */
export async function flushOutbox(
  entries: readonly CheckoffOutboxEntry[],
  send: (entry: CheckoffOutboxEntry) => Promise<unknown>,
): Promise<FlushResult> {
  let sentCount = 0;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]!;
    // Legacy/malformed records survive in their read-only source for guarded
    // recovery, but replay must never attach an epoch or send them blindly.
    if (!isCurrentCheckoffOutboxEntry(entry)) continue;
    try {
      await send(entry);
      sentCount++;
    } catch (error) {
      if (isRetryableCheckoffError(error)) {
        return { remaining: entries.slice(i), sentCount };
      }
      // 404 means the target disappeared. Removing every target-scoped entry
      // prevents a rebase/retry loop while preserving unrelated work.
      if (
        typeof error === "object" &&
        error !== null &&
        (error as { status?: unknown }).status === 404
      ) {
        const remaining = entries.filter(
          (candidate) =>
            candidate.groceryListId !== entry.groceryListId ||
            candidate.itemId !== entry.itemId,
        );
        return { remaining, sentCount };
      }
      // Policy-invalid and other definite server rejections drop the exact
      // v3 intent; they do not create a legacy rebase candidate.
    }
  }
  return { remaining: [], sentCount };
}

/**
 * Loads the persisted queue. Defensive on every axis: storage may be
 * unavailable (privacy mode), the key absent, the JSON corrupt, or the
 * shape stale from an older build -- all of those return [] (or the valid
 * subset) rather than throwing.
 */
export function loadOutbox(
  storage:
    | (Partial<EnumerableCheckoffStorage> &
        Partial<Pick<Storage, "setItem" | "removeItem">>)
    | null
    | undefined,
): CheckoffOutboxEntry[] {
  return loadOutboxSources(storage).flatMap((source) => source.entries);
}

function isValidAnalyticsSnapshot(
  value: GroceryCheckoffAnalyticsSnapshot | undefined,
): boolean {
  return (
    value === undefined ||
    (typeof value === "object" &&
      value !== null &&
      typeof value.mealPlanId === "string" &&
      Array.isArray(value.generatedItemIds) &&
      value.generatedItemIds.every((id) => typeof id === "string") &&
      Array.isArray(value.uncheckedGeneratedItemIds) &&
      value.uncheckedGeneratedItemIds.every((id) => typeof id === "string"))
  );
}

/** Mirrors the queue to storage; silently a no-op when storage is unusable. */
export function saveOutbox(
  storage:
    | (Partial<EnumerableCheckoffStorage> &
        Pick<Storage, "setItem" | "removeItem">)
    | null
    | undefined,
  entries: readonly CheckoffOutboxEntry[],
): void {
  try {
    if (!storage) return;
    const getItem = storage.getItem;
    const setItem = storage.setItem;
    const removeItem = storage.removeItem;
    // A publisher must never overwrite a recovery-owned aggregate. The caller
    // keeps its in-memory intent and retries through its normal later write.
    if (typeof getItem !== "function") return;
    const current = [{ key: CHECKOFF_OUTBOX_STORAGE_KEY, raw: getItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY) }];
    const journals = outboxJournals(storage);
    if (!journals) return;
    for (const journal of journals) {
      const liveRaw = getItem.call(storage, journal.key);
      if (liveRaw === null) continue;
      if (liveRaw !== journal.raw) return;
      if (!journal.value) return;
      if (!journal.value.snapshots.some((snapshot) => snapshot.key === CHECKOFF_OUTBOX_STORAGE_KEY)) continue;
      if (journal.value.phase !== "committed" || !clearOutboxCommittedFenceForSuccessor(storage, { key: journal.key, raw: journal.raw, value: journal.value }, current)) return;
    }
    const eligibleEntries = entries.filter(
      (entry): entry is CheckoffOutboxEntryV2 =>
        isCurrentCheckoffOutboxEntry(entry),
    );
    if (eligibleEntries.length === 0) {
      if (typeof removeItem !== "function") return;
      removeItem.call(storage, CHECKOFF_OUTBOX_STORAGE_KEY);
    } else {
      if (typeof setItem !== "function") return;
      setItem.call(
        storage,
        CHECKOFF_OUTBOX_STORAGE_KEY,
        JSON.stringify(eligibleEntries),
      );
    }
  } catch {
    // Quota/privacy-mode failures degrade to in-memory-only queueing.
  }
}

export function samePersistedCheckoffOperation(
  left: CheckoffOutboxEntry,
  right: CheckoffOutboxEntry,
): boolean {
  return (
    persistedCheckoffOperationIdentity(left) ===
    persistedCheckoffOperationIdentity(right)
  );
}

export function persistedCheckoffOperationIdentity(
  entry: CheckoffOutboxEntry,
): string {
  return (
    entry.operationId ??
    `legacy:${entry.groceryListId}:${entry.itemId}:${entry.checked}:${entry.queuedAt}`
  );
}

export function mergeGroceryCheckoffAnalyticsProgress(
  current: GroceryCheckoffAnalyticsSnapshot | undefined,
  incoming: GroceryCheckoffAnalyticsSnapshot | undefined,
  options: { authoritativeMembership?: boolean } = {},
): GroceryCheckoffAnalyticsSnapshot | undefined {
  const normalize = (
    snapshot: GroceryCheckoffAnalyticsSnapshot,
  ): GroceryCheckoffAnalyticsSnapshot => {
    const generatedItemIds = [...new Set(snapshot.generatedItemIds)];
    const generated = new Set(generatedItemIds);
    const unchecked = new Set(snapshot.uncheckedGeneratedItemIds);
    return {
      mealPlanId: snapshot.mealPlanId,
      generatedItemIds,
      uncheckedGeneratedItemIds: generatedItemIds.filter(
        (itemId) => generated.has(itemId) && unchecked.has(itemId),
      ),
    };
  };

  if (!current) return incoming ? normalize(incoming) : undefined;
  if (!incoming) return normalize(current);
  const normalizedCurrent = normalize(current);
  const normalizedIncoming = normalize(incoming);
  if (current.mealPlanId !== incoming.mealPlanId) return normalizedIncoming;

  if (options.authoritativeMembership) {
    const currentUnchecked = new Set(
      normalizedCurrent.uncheckedGeneratedItemIds,
    );
    const acknowledgedCurrent = new Set(
      normalizedCurrent.generatedItemIds.filter(
        (itemId) => !currentUnchecked.has(itemId),
      ),
    );
    const authoritativeUnchecked = new Set(
      normalizedIncoming.uncheckedGeneratedItemIds,
    );
    return {
      mealPlanId: normalizedIncoming.mealPlanId,
      generatedItemIds: normalizedIncoming.generatedItemIds,
      uncheckedGeneratedItemIds: normalizedIncoming.generatedItemIds.filter(
        (itemId) =>
          authoritativeUnchecked.has(itemId) &&
          !acknowledgedCurrent.has(itemId),
      ),
    };
  }

  const generatedItemIds = [
    ...normalizedCurrent.generatedItemIds,
    ...normalizedIncoming.generatedItemIds.filter(
      (itemId) => !normalizedCurrent.generatedItemIds.includes(itemId),
    ),
  ];
  const acknowledgedItemIds = new Set<string>();
  for (const snapshot of [normalizedCurrent, normalizedIncoming]) {
    const unchecked = new Set(snapshot.uncheckedGeneratedItemIds);
    for (const itemId of snapshot.generatedItemIds) {
      if (!unchecked.has(itemId)) acknowledgedItemIds.add(itemId);
    }
  }

  return {
    mealPlanId: incoming.mealPlanId,
    generatedItemIds,
    uncheckedGeneratedItemIds: generatedItemIds.filter(
      (itemId) => !acknowledgedItemIds.has(itemId),
    ),
  };
}

/**
 * Applies one hook instance's queue transition without overwriting work that
 * another mounted instance persisted after this instance's last read.
 */
export function persistOutboxUpdate(
  storage:
    | (Partial<EnumerableCheckoffStorage> &
        Pick<Storage, "setItem" | "removeItem">)
    | null,
  previous: readonly CheckoffOutboxEntry[],
  next: readonly CheckoffOutboxEntry[],
  analyticsProgressByListId?: ReadonlyMap<
    string,
    GroceryCheckoffAnalyticsSnapshot
  >,
  authoritativeMembershipListIds?: ReadonlySet<string>,
  baselineFor?: (
    groceryListId: string,
    itemId: string,
  ) => ServerConfirmedCheckoff | undefined,
): CheckoffOutboxEntry[] {
  const eligiblePrevious = [...previous];
  const eligibleNext = [...next];
  if (!storage) return eligibleNext;

  const removed = eligiblePrevious.filter(
    (entry) =>
      !eligibleNext.some((candidate) =>
        samePersistedCheckoffOperation(entry, candidate),
      ),
  );
  const candidates = [
    ...loadOutbox(storage).filter(
      (entry) =>
        !removed.some((candidate) =>
          samePersistedCheckoffOperation(entry, candidate),
        ),
    ),
    ...eligibleNext,
  ];
  const quarantined: CheckoffOutboxEntry[] = [];
  const admittedCandidates = baselineFor
    ? candidates.filter((entry) => {
        const admission = admitCheckoffOutboxEntry(
          entry,
          baselineFor(entry.groceryListId, entry.itemId),
        );
        if (admission.kind === "quarantine") quarantined.push(entry);
        return admission.kind === "admit";
      })
    : candidates;
  const merged: CheckoffOutboxEntry[] = [];
  // Only mutually admitted candidates compete for a target winner. A higher
  // durable quarantine remains separate recovery input and cannot replace the
  // actionable confirmed-epoch intent.
  for (const entry of admittedCandidates) {
    const index = merged.findIndex(
      (candidate) =>
        candidate.groceryListId === entry.groceryListId &&
        candidate.itemId === entry.itemId,
    );
    if (index === -1) {
      merged.push(entry);
      continue;
    }

    const current = merged[index]!;
    const entryIsAtLeastAsFresh = isEntryAtLeastAsFresh(entry, current);
    const winner = entryIsAtLeastAsFresh ? entry : current;
    const analyticsSnapshot = entryIsAtLeastAsFresh
      ? mergeGroceryCheckoffAnalyticsProgress(
          current.analyticsSnapshot,
          entry.analyticsSnapshot,
        )
      : mergeGroceryCheckoffAnalyticsProgress(
          entry.analyticsSnapshot,
          current.analyticsSnapshot,
        );
    merged[index] = analyticsSnapshot
      ? { ...winner, analyticsSnapshot }
      : winner;
  }

  const mergedProgressByListId = new Map<
    string,
    GroceryCheckoffAnalyticsSnapshot
  >();
  for (const entry of merged) {
    const progress = mergeGroceryCheckoffAnalyticsProgress(
      mergedProgressByListId.get(entry.groceryListId),
      entry.analyticsSnapshot,
    );
    if (progress) mergedProgressByListId.set(entry.groceryListId, progress);
  }
  analyticsProgressByListId?.forEach((progress, groceryListId) => {
    mergedProgressByListId.set(
      groceryListId,
      mergeGroceryCheckoffAnalyticsProgress(
        mergedProgressByListId.get(groceryListId),
        progress,
        {
          authoritativeMembership:
            authoritativeMembershipListIds?.has(groceryListId) === true,
        },
      ) ?? progress,
    );
  });

  const admitted = [...merged, ...quarantined];
  const reconciled = admitted.map((entry) => {
    const analyticsSnapshot = mergedProgressByListId.get(entry.groceryListId);
    return analyticsSnapshot ? { ...entry, analyticsSnapshot } : entry;
  });

  saveOutbox(storage, reconciled);
  return reconciled;
}

function isEntryAtLeastAsFresh(
  candidate: CheckoffOutboxEntry,
  current: CheckoffOutboxEntry,
): boolean {
  if (
    isCurrentCheckoffOutboxEntry(candidate) &&
    isCurrentCheckoffOutboxEntry(current)
  ) {
    const epoch = compareCheckoffEpochs(
      candidate.checkoffEpoch,
      current.checkoffEpoch,
    );
    if (epoch !== 0) return epoch > 0;
    return (
      compareCheckoffOperationFreshness(
        checkoffOperationFreshness(candidate),
        checkoffOperationFreshness(current),
      ) >= 0
    );
  }
  if (isCurrentCheckoffOutboxEntry(candidate)) return true;
  if (isCurrentCheckoffOutboxEntry(current)) return false;
  return (
    compareCheckoffOperationFreshness(
      checkoffOperationFreshness(candidate),
      checkoffOperationFreshness(current),
    ) >= 0
  );
}
